/**
 * DeclarerAbsenceModal.tsx — Déclaration d'une absence par un responsable
 * depuis la présence (Bloc 2). Crée une absence RH validée directement et
 * remonte les tâches à réaffecter.
 */
import { useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';

interface Props {
  agent: { id: string; fullName: string };
  /** jour cliqué (YYYY-MM-DD) — sert de date de début par défaut */
  jourISO: string;
  service?: string;
  onClose: () => void;
  onDone: (tachesEnConflit: number) => void;
}

/**
 * Motifs proposés au responsable. Alignés (lot 2) sur les types que l'agent voit
 * dans son propre planning (RH_ABSENCE_TYPES, mig 223) : il manquait accident du
 * travail, formation, enfant malade et événement familial.
 *
 * `typeValide` reste la qualification RH de la base (conges_payes | justifiee |
 * injustifiee) : ce modal crée une absence DÉJÀ VALIDÉE, c'est le responsable qui
 * tranche. On ne change pas cette mécanique.
 */
const MOTIFS: Array<{ value: string; label: string; typeValide: 'conges_payes' | 'justifiee' | 'injustifiee' }> = [
  { value: 'Maladie', label: 'Arrêt maladie', typeValide: 'justifiee' },
  { value: 'Accident du travail', label: 'Accident du travail', typeValide: 'justifiee' },
  { value: 'Congé', label: 'Congé payé', typeValide: 'conges_payes' },
  { value: 'RTT', label: 'RTT', typeValide: 'conges_payes' },
  { value: 'Formation', label: 'Formation', typeValide: 'justifiee' },
  { value: 'Enfant malade', label: 'Enfant malade', typeValide: 'justifiee' },
  { value: 'Événement familial', label: 'Événement familial', typeValide: 'justifiee' },
  { value: 'Autorisation d\'absence', label: 'Autorisation d\'absence', typeValide: 'justifiee' },
  { value: 'Récupération', label: 'Récupération', typeValide: 'justifiee' },
  { value: 'Absence justifiée', label: 'Absence justifiée (autre)', typeValide: 'justifiee' },
  { value: 'Absence injustifiée', label: 'Absence injustifiée', typeValide: 'injustifiee' },
  { value: 'Autre', label: 'Autre', typeValide: 'justifiee' },
];

export function DeclarerAbsenceModal({ agent, jourISO, service, onClose, onDone }: Props) {
  const [motifIdx, setMotifIdx] = useState(0);
  const [dateDebut, setDateDebut] = useState(jourISO);
  const [dateFin, setDateFin] = useState(jourISO);
  const [precision, setPrecision] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valider = async () => {
    if (dateFin < dateDebut) { setError('La date de fin doit être après le début.'); return; }
    setBusy(true); setError(null);
    try {
      const motif = MOTIFS[motifIdx]!;
      const motifTexte = precision.trim() ? `${motif.value} — ${precision.trim()}` : motif.value;
      const res = await api.rh.declarerAbsence({
        agentUserId: agent.id,
        dateDebut,
        dateFin,
        motif: motifTexte,
        typeValide: motif.typeValide,
        service,
      });
      onDone(res.tachesEnConflit);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur lors de la déclaration');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} title={`Déclarer une absence — ${agent.fullName.split(' ')[0]}`} size="sm">
      <div className="flex flex-col gap-3.5">
        <div>
          <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Motif</label>
          <select value={motifIdx} onChange={(e) => setMotifIdx(Number(e.target.value))} className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink">
            {MOTIFS.map((m, i) => <option key={m.value} value={i}>{m.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Du</label>
            <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink" />
          </div>
          <div>
            <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Au</label>
            <input type="date" value={dateFin} min={dateDebut} onChange={(e) => setDateFin(e.target.value)} className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink" />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Précision (optionnel)</label>
          <input value={precision} onChange={(e) => setPrecision(e.target.value)} placeholder="Ex. arrêt jusqu'au 10, certificat reçu…" className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink" />
        </div>

        <p className="text-[11px] text-ink-dim">L'absence est enregistrée immédiatement et transmise à la brique RH (Absences). Si des tâches sont affectées sur la période, le responsable en est alerté.</p>

        {error && <p className="text-xs text-urgent">{error}</p>}

        <div className="flex justify-end gap-2.5">
          <button type="button" onClick={onClose} className="rounded-r-md border border-border-hi bg-bg-2/60 px-4 py-2 text-sm font-semibold text-ink">Annuler</button>
          <button type="button" onClick={valider} disabled={busy} className="rounded-r-md bg-tech px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? '…' : 'Déclarer l\'absence'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
